import { Card, CardBody, Divider } from '@nextui-org/react'
import { FC } from 'react'
import type { GroupDetail } from '@/types/domain'
import { SchoolCard } from './SchoolCard'

interface GroupCardContainerProps {
  group: GroupDetail
}

/**
 * GroupCardContainer Component
 *
 * Displays a group with all its assigned schools and tanks.
 * Replaces the old Batch-based SchoolCardContainer.
 *
 * @param group - Group with full details (schools and tanks)
 */
export const GroupCardContainer: FC<GroupCardContainerProps> = ({ group }) => {
  // Extract school-tank pairs from group.items (Domain model)
  const items = group.items || []

  // Sort by tank name
  const sortedItems = [...items].sort((a, b) => {
    const tankNameA = a.tank?.name?.toLowerCase() || ''
    const tankNameB = b.tank?.name?.toLowerCase() || ''
    return tankNameA.localeCompare(tankNameB)
  })

  // Create SchoolCard components
  // Pass school/tank data directly to SchoolCard
  const createCardBlocks = sortedItems
    .filter((item) => item.school && item.tank && item.school.id && item.tank.id)
    .map((item) => (
      <span key={item.school.id + '-' + item.tank.id}>
        <SchoolCard
          schoolId={item.school.id}
          tankId={item.tank.id}
          school={item.school}
          tank={item.tank}
        />
      </span>
    ))

  // Find the latest start_date in the group (replaces Batch startDate)
  const latestStartDate = sortedItems.length > 0
    ? Math.max(...sortedItems
        .filter(item => item.school?.startDate)
        .map(item => new Date(item.school.startDate).getTime())
      )
    : 0

  // Format date as YYYY-MM-DD
  const formatDate = (timestamp: number): string => {
    if (timestamp === 0) return ''
    const date = new Date(timestamp)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const dateString = formatDate(latestStartDate)

  return (
    <div
      className="mt-6 md:mt-10 first:mt-0 max-w-full overflow-x-hidden"
      data-testid="group-card"
    >
      <div className="flex flex-row justify-between items-start gap-2 my-1">
        {/* Mobile layout */}
        <div className="sm:hidden grid grid-cols-2 items-center w-full">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="ml-1 text-tdplindigo text-lg font-semibold truncate">
                {group.name}
              </div>
            </div>
            {dateString && (
              <div className="ml-1 text-tdpldarkgrey text-sm text-opacity-60">
                {dateString}
              </div>
            )}
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden sm:flex flex-wrap items-center gap-2 md:gap-5">
          <div className="ml-1 text-tdplindigo text-lg md:text-[1.25rem]">
            {group.name}
          </div>
          {dateString && (
            <>
              <Divider
                className="hidden md:block w-[1px] h-[60%] mt-1 mx-2 bg-tdpldarkgrey bg-opacity-60"
                orientation="vertical"
              />
              <div className="text-tdpldarkgrey text-base md:text-[1.25rem] text-opacity-60">
                {dateString}
              </div>
            </>
          )}
        </div>
      </div>

      <Card
        className="bg-white border-opacity-50 border-1 border-tdpldarkgrey p-2 overflow-x-hidden rounded-lg"
        shadow="none"
        radius="lg"
        style={{ borderRadius: '0.75rem' }}
      >
        <CardBody className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,16rem)] justify-start gap-3 md:gap-4 p-3 md:p-6">
          {createCardBlocks}
        </CardBody>
      </Card>
    </div>
  )
}

export default GroupCardContainer
